/* Preferencias de cada persona: tema, color de acento, fondo y transparencia.
 *
 * Se guardan POR PERSONA, no globalmente. Es lo que hace que la app se sienta
 * propia y no una herramienta compartida — y en una empresa donde varios usan
 * la misma computadora, evita que el que entra después se encuentre el tema
 * del anterior.
 *
 * DÓNDE VIVEN: en la base, para que sigan a la persona y no al navegador (ver
 * services/preferencias.js). Lo de acá es una copia local, y sirve para una
 * sola cosa: pintar el tema en el primer instante, sin esperar a la red. Sin
 * esa copia, cada carga empieza en claro y salta a oscuro medio segundo
 * después, que se ve como si la app se hubiera roto.
 *
 * Que sea copia y no fuente importa cuando las dos no coinciden: gana siempre
 * lo que dice la base.
 */

import { baseDeTema, existeTema } from "../shared/registroTemas";

const CLAVE = "prefs-flota";

export const ACENTOS = [
  ["Ruta", "#1B4D8F", "#64A2ED"],
  ["Pino", "#1B6B4A", "#4FC98C"],
  ["Ladrillo", "#9B3B2E", "#EE8A7F"],
  ["Ciruela", "#5B3A78", "#B18BDC"],
  ["Grafito", "#3A4453", "#96A3B5"],
];

export const PREF_DEF = {
  tema: "auto",
  acento: "#1B4D8F",
  acentoOscuro: "#64A2ED",
  fondo: null,
  // Dónde queda anclada la imagen, en porcentaje. 50/50 es el centro, que es
  // lo que hacía antes sin poder cambiarlo: con `cover`, una foto vertical
  // recortada al centro suele cortar justo lo que uno quería ver.
  fondoX: 50,
  fondoY: 50,
  // Cuánto se agranda. 100 es "que cubra"; más grande permite acercarse a una
  // parte de la foto en vez de tragarse toda la composición.
  fondoZoom: 100,
  blur: true,
};

function leerTodas() {
  try {
    return JSON.parse(localStorage.getItem(CLAVE) || "{}");
  } catch (e) {
    // Modo privado o almacenamiento bloqueado. No es motivo para romper: la
    // app funciona igual, sólo que las preferencias no sobreviven al cierre.
    return {};
  }
}

export function leerPrefs(id) {
  const todas = leerTodas();
  return { ...PREF_DEF, ...(todas[id || "anon"] || {}) };
}

/* Guarda la copia local. Nunca devuelve error hacia arriba: si el navegador no
 * deja escribir (modo privado, disco lleno), lo único que se pierde es el
 * arranque instantáneo del tema. El dato de verdad ya está en la base. */
export function guardarPrefsLocal(id, prefs) {
  try {
    const todas = leerTodas();
    todas[id || "anon"] = prefs;
    localStorage.setItem(CLAVE, JSON.stringify(todas));
  } catch (e) {
    // Sin copia local la app anda igual, sólo que el tema tarda un parpadeo.
  }
}

/* Hay preferencias viejas guardadas en este navegador que nunca llegaron a la
 * base. Se usa una sola vez, para no hacerle perder a nadie lo que ya tenía
 * configurado cuando esto vivía sólo acá. */
export function hayPrefsLocales(id) {
  const todas = leerTodas();
  return Object.keys(todas[id || "anon"] || {}).length > 0;
}

/* Escribe las preferencias en el documento. Toca variables CSS y clases del
 * body en vez de re-renderizar: el tema tiene que aplicarse también a lo que
 * está fuera de React (el fondo de la página, la barra del navegador). */
/* Los themes de identidad propia (ej. Industrial) traen sus fuentes de Google.
   Se cargan SÓLO cuando el tema se activa, para no penalizar a los que usan el
   default con un font que no van a ver. Se inyecta una vez. */
function cargarFuentesDeTema(tema) {
  if (tema !== "industrial" || document.getElementById("fuentes-industrial")) return;
  const l = document.createElement("link");
  l.id = "fuentes-industrial";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700"
    + "&family=Barlow+Condensed:wght@600;700;800"
    + "&family=IBM+Plex+Mono:wght@400;500;600&display=swap";
  document.head.appendChild(l);
}

export function aplicarPrefs(prefs) {
  const p = { ...PREF_DEF, ...(prefs || {}) };
  const raiz = document.documentElement;

  // Preview por URL: ?theme=<id> pinta ese tema sin tocar lo guardado, para
  // revisar uno oculto (ej. Industrial) con un link, antes de exponerlo.
  let override = null;
  try {
    override = new URLSearchParams(window.location.search).get("theme");
  } catch (e) { /* sin window.location no hay preview, y no pasa nada */ }
  const tema = override && existeTema(override) ? override : p.tema;

  // "auto" (y cualquier id que no conozcamos) no fija atributo: manda el
  // sistema. Un theme con identidad propia (light, dark, industrial…) sí.
  if (tema === "auto" || !existeTema(tema)) raiz.removeAttribute("data-theme");
  else raiz.setAttribute("data-theme", tema);
  cargarFuentesDeTema(tema);

  // El acento viene en par claro/oscuro: el mismo azul sobre fondo negro se
  // apaga, así que cada tema usa su variante. La base del theme (del registro)
  // dice si el fondo es oscuro; sin base fija, manda el sistema.
  const base = baseDeTema(tema);
  const oscuro =
    base === "dark" ||
    (base === null &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);
  const tinte = oscuro ? p.acentoOscuro : p.acento;

  raiz.style.setProperty("--tinte", tinte);
  raiz.style.setProperty("--tinte-vivo", tinte);
  raiz.style.setProperty(
    "--tinte-lavado",
    `color-mix(in srgb, ${tinte} 10%, transparent)`
  );

  document.body.classList.toggle("con-fondo", !!p.fondo);
  document.body.classList.toggle("sin-blur", !p.blur);

  if (p.fondo) {
    document.body.style.setProperty("--fondo-img", `url(${p.fondo})`);
    document.body.style.setProperty(
      "--fondo-pos",
      `${p.fondoX ?? 50}% ${p.fondoY ?? 50}%`
    );
    // `cover` garantiza que no queden franjas vacías; el zoom se aplica encima
    // como porcentaje del alto, que es lo que permite acercarse sin destapar
    // los bordes.
    const z = p.fondoZoom ?? 100;
    document.body.style.setProperty(
      "--fondo-tam",
      z === 100 ? "cover" : `auto ${z}%`
    );
  } else {
    document.body.style.removeProperty("--fondo-img");
    document.body.style.removeProperty("--fondo-pos");
    document.body.style.removeProperty("--fondo-tam");
  }
}

/* El par claro/oscuro del acento elegido. Se busca por el valor claro, que es
 * el que se guarda como identificador. */
export function parDeAcento(claro) {
  const encontrado = ACENTOS.find((a) => a[1] === claro);
  return encontrado
    ? { acento: encontrado[1], acentoOscuro: encontrado[2] }
    : { acento: PREF_DEF.acento, acentoOscuro: PREF_DEF.acentoOscuro };
}
